import React from 'react';
import { Scale, Globe } from 'lucide-react';
import { ArkoTrustBadge } from './ArkoBotSources';
import { getAnswerPathStyleFromTrace, modeLabel, type ReasoningTrace } from './reasoningTrace';
import {
  hasJudicatureProofData,
  isVerifiedJudicatureProof,
  type JudicatureProof,
} from './judicatureProof';
import {
  hasWebSearchProofData,
  type WebSearchProof,
} from './webSearchProof';
import type { LexProof } from './lexProof';

interface ArkoAnswerLabelsProps {
  text: string;
  lexProof?: LexProof | null;
  judProof?: JudicatureProof | null;
  webProof?: WebSearchProof | null;
  reasoningTrace?: ReasoningTrace | null;
  /** Zusätzliches Intent-Label aus Trace (Modus/Heuristik). */
  showTraceHint?: boolean;
}

/**
 * Phase 1.4 / §13.07 — sichtbare Antwort-Labels pro Bot-Nachricht
 * (Pfad + Vertrauen), nicht nur im eingeklappten Trace-Drawer.
 */
export const ArkoAnswerLabels: React.FC<ArkoAnswerLabelsProps> = ({
  text,
  lexProof,
  judProof,
  webProof,
  reasoningTrace,
  showTraceHint = true,
}) => {
  const pathStyle = getAnswerPathStyleFromTrace(reasoningTrace);
  const rawMode =
    showTraceHint && reasoningTrace?.mode?.trim()
      ? reasoningTrace.mode.trim()
      : '';
  const modeHint = rawMode
    ? (/lexcore/i.test(modeLabel(rawMode)) ? 'Normen' : modeLabel(rawMode))
    : null;
  const hasJudProof = hasJudicatureProofData(judProof);
  const verifiedJud = isVerifiedJudicatureProof(judProof);
  const judCount = judProof?.cases.length ?? 0;
  const hasWebProof = hasWebSearchProofData(webProof);
  const webOk = webProof?.status === 'ok' && (webProof.sources?.length ?? 0) > 0;
  const webCount = webProof?.sources.length ?? 0;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {pathStyle ? (
        <span
          className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
          style={{
            borderColor: pathStyle.borderColor,
            color: pathStyle.labelColor,
          }}
          title={`Antwort-Pfad: ${pathStyle.label}`}
        >
          {pathStyle.label}
        </span>
      ) : null}
      {hasJudProof ? (
        <span
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
            verifiedJud
              ? 'border-violet-500/30 bg-violet-500/10 text-violet-200'
              : 'border-white/[0.1] bg-white/[0.04] text-slate-400'
          }`}
          title={
            verifiedJud
              ? 'OGH-Treffer aus dem aktuellen Judikatur-Bestand.'
              : 'Kein belastbarer OGH-Treffer im aktuellen Judikatur-Bestand.'
          }
        >
          <Scale className="w-3 h-3" aria-hidden />
          OGH-Judikatur · {verifiedJud ? `${judCount} Treffer` : 'kein Treffer'}
        </span>
      ) : null}
      {hasWebProof ? (
        <span
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
            webOk
              ? 'border-sky-500/30 bg-sky-500/10 text-sky-200'
              : 'border-white/[0.1] bg-white/[0.04] text-slate-400'
          }`}
          title={
            webOk
              ? 'Öffentliche Treffer über das ARKORBIT-Such-Gate (URLs in der Klappe).'
              : 'Keine belastbare öffentliche Web-Quelle — nichts erfunden.'
          }
        >
          <Globe className="w-3 h-3" aria-hidden />
          Recherche · {webOk ? `${webCount} Quelle${webCount === 1 ? '' : 'n'}` : 'kein Treffer'}
        </span>
      ) : null}
      {!hasJudProof || lexProof ? <ArkoTrustBadge text={text} lexProof={lexProof} lawyerFacing /> : null}
      {modeHint ? (
        <span
          className="text-[9px] text-slate-500 truncate max-w-[12rem]"
          title={modeHint}
        >
          · {modeHint}
        </span>
      ) : null}
    </div>
  );
};
